import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, map, throwError } from 'rxjs';
import { IAppConfig } from '../shared/interfaces/app-config';
import { AppConfigService } from './app-config.service';
import { IReferenceData } from '../shared/interfaces/form/reference-data.model';
import { sortBy } from 'lodash';
import { Location } from '../shared/models/location';
import { handleError } from '../shared/functions/error-handler';
import { LcgmsLocationDetails } from '../shared/interfaces/lcgms-location';
import { IAssessmentPlanning } from '../modules/evaluations/interfaces/assessment-planning';

@Injectable({
  providedIn: 'root',
})
export class ReferenceService {
  config: IAppConfig;

  isEditingStudentInfo$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  constructor(
    private http: HttpClient,
    private appConfigService: AppConfigService
  ) {
    this.config = this.appConfigService.getConfig();
  }

  getDropDownValues(ddName: string): Observable<IReferenceData[]> {
    return this.http
      .get<IReferenceData[]>(`${this.config.apiUrl}/ReferenceValue/${ddName}`)
      .pipe(
        catchError(this.handleError),
        map((data: IReferenceData[]) => sortBy(data, 'sortOrder'))
      );
  }

  getAssessmentDropDownValues(
    type: string,
    id: string
  ): Observable<IAssessmentPlanning> {
    return this.http
      .get<IAssessmentPlanning>(
        `${this.config.apiUrl}/${type}/GetAssessmentPlanningByReferral/` + id
      )
      .pipe(catchError(handleError));
  }

  getLocations() {
    return this.http
      .get<Location[]>(`${this.config.apiUrl}/ReferenceValue/Locations`)
      .pipe(catchError(this.handleError));
  }

  getLcgmsLocations() {
    return this.http
      .get<Location[]>(`${this.config.apiUrl}/User/Locations`)
      .pipe(catchError(this.handleError));
  }

  getDohmhAgencies(): Observable<Location[]> {
    return this.http.get<unknown[]>(`${this.config.apiUrl}/User/Agencies`).pipe(
      map((data) => data?.map((d) => new Location(d))),
      catchError(this.handleError)
    );
  }

  getDohmhAgencyData(agencyId: string): Observable<LcgmsLocationDetails> {
    return this.http
      .get<LcgmsLocationDetails>(
        `${this.config.apiUrl}/User/GetAgency/${agencyId}`
      )
      .pipe(catchError(this.handleError));
  }

  getAttendeesList(referralSK: any, caseStage: any): Observable<any> {
    return this.http
      .get<IReferenceData[]>(
        `${this.config.apiUrl}/IEPSummary/GetAttendeesList/${referralSK}/${caseStage}`
      )
      .pipe(catchError(this.handleError));
  }

  // Error handling
  handleError(error: any): Observable<never> {
    let errorMessage: string = '';
    if (error.error instanceof ErrorEvent) {
      // Get client-side error
      errorMessage = error.error.message;
    } else {
      // Get server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    return throwError(() => {
      return errorMessage;
    });
  }
}
